import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { uuid } from "@jkast90/kast-common/storage";
import { shopService, type ShopItem } from "../../services/shopService";

export interface CartLine {
  line_id: string;
  item_id: string;
  name_snapshot: string;
  unit_price_cents_snapshot: number;
  photo_public_url?: string;
  variant_selections: Record<string, string>;
  quantity: number;
  /**
   * Set when the last check against /shop/items found the line can't be
   * ordered as it stands (sold out, or its options no longer exist). The
   * line stays so the customer sees it; checkout is blocked until it goes.
   */
  unavailable?: string;
}

export interface CartState {
  lines: CartLine[];
}

export const CART_STORAGE_KEY = "hnt_cart_v1";

function sameSelections(a: Record<string, string>, b: Record<string, string>): boolean {
  const ak = Object.keys(a).sort();
  const bk = Object.keys(b).sort();
  if (ak.length !== bk.length) return false;
  for (let i = 0; i < ak.length; i++) {
    if (ak[i] !== bk[i]) return false;
    if (a[ak[i]] !== b[bk[i]]) return false;
  }
  return true;
}

function isStringRecord(v: unknown): v is Record<string, string> {
  return !!v && typeof v === "object" && !Array.isArray(v) && Object.values(v).every((x) => typeof x === "string");
}

/** One saved line, or null when it isn't the shape this code writes. */
function parseLine(v: unknown): CartLine | null {
  if (!v || typeof v !== "object") return null;
  const l = v as Record<string, unknown>;
  if (typeof l.line_id !== "string" || !l.line_id) return null;
  if (typeof l.item_id !== "string" || !l.item_id) return null;
  if (typeof l.name_snapshot !== "string") return null;
  const price = l.unit_price_cents_snapshot;
  if (typeof price !== "number" || !Number.isInteger(price) || price < 0) return null;
  if (!isStringRecord(l.variant_selections)) return null;
  const qty = l.quantity;
  if (typeof qty !== "number" || !Number.isFinite(qty)) return null;
  if (l.photo_public_url !== undefined && typeof l.photo_public_url !== "string") return null;
  return {
    line_id: l.line_id,
    item_id: l.item_id,
    name_snapshot: l.name_snapshot,
    unit_price_cents_snapshot: price,
    ...(l.photo_public_url ? { photo_public_url: l.photo_public_url as string } : {}),
    variant_selections: { ...l.variant_selections },
    quantity: Math.max(1, Math.min(99, Math.floor(qty))),
    ...(typeof l.unavailable === "string" && l.unavailable ? { unavailable: l.unavailable } : {}),
  };
}

/**
 * A saved cart (a JSON string from localStorage, or null). Anything that
 * isn't the expected shape is dropped line by line, so an old or hand-edited
 * value can't crash the cart page or send junk to checkout.
 */
export function parseCart(raw: string | null): CartState {
  if (!raw) return { lines: [] };
  try {
    const parsed: unknown = JSON.parse(raw);
    const lines = (parsed as { lines?: unknown })?.lines;
    if (!Array.isArray(lines)) return { lines: [] };
    return { lines: lines.map(parseLine).filter((l): l is CartLine => l !== null) };
  } catch {
    return { lines: [] };
  }
}

function loadInitial(): CartState {
  if (typeof window === "undefined") return { lines: [] };
  try {
    return parseCart(window.localStorage.getItem(CART_STORAGE_KEY));
  } catch {
    return { lines: [] };
  }
}

export type CartNotice =
  | { kind: "price"; name: string; from: number; to: number }
  | { kind: "removed"; name: string }
  | { kind: "unavailable"; name: string; reason: string };

function selectionProblem(item: ShopItem, sel: Record<string, string>): string | null {
  const groups = item.variants ?? [];
  for (const g of groups) {
    const chosen = sel[g.name];
    if (!chosen || !g.options.includes(chosen)) {
      return `The ${g.name.toLowerCase()} you picked is no longer offered.`;
    }
  }
  if (Object.keys(sel).some((k) => !groups.some((g) => g.name === k))) {
    return "The options you picked have changed.";
  }
  return null;
}

/**
 * Checks saved lines against the live catalogue. Items no longer listed are
 * dropped; sold-out items and lines whose options are gone are flagged; the
 * name, photo and price are brought up to date, since the server charges
 * the current price. Returns the new lines and what the customer should be
 * told.
 */
export function reconcileLines(lines: CartLine[], items: ShopItem[]): { lines: CartLine[]; notices: CartNotice[] } {
  const byId = new Map(items.map((i) => [i.item_id, i]));
  const notices: CartNotice[] = [];
  const next: CartLine[] = [];
  for (const line of lines) {
    const item = byId.get(line.item_id);
    if (!item) {
      notices.push({ kind: "removed", name: line.name_snapshot });
      continue;
    }
    const reason = !item.in_stock ? "It's sold out." : selectionProblem(item, line.variant_selections);
    if (reason && reason !== line.unavailable) {
      notices.push({ kind: "unavailable", name: item.name, reason });
    }
    if (item.price_cents !== line.unit_price_cents_snapshot) {
      notices.push({
        kind: "price",
        name: item.name,
        from: line.unit_price_cents_snapshot,
        to: item.price_cents,
      });
    }
    const updated: CartLine = {
      ...line,
      name_snapshot: item.name,
      unit_price_cents_snapshot: item.price_cents,
      photo_public_url: item.photo_public_url || line.photo_public_url,
    };
    if (reason) updated.unavailable = reason;
    else delete updated.unavailable;
    next.push(updated);
  }
  return { lines: next, notices };
}

/**
 * Re-reads /shop/items and reconciles the cart with it (on opening the cart
 * or checkout). If the catalogue can't be read the cart is left alone; the
 * server still validates the order.
 */
export const revalidateCart = createAsyncThunk<CartNotice[], void, { state: { cart: CartState } }>(
  "cart/revalidate",
  async (_, { dispatch, getState }) => {
    const items = await shopService.listItems();
    // Reconcile against the cart as it is now, not as it was when the fetch
    // started, so an edit made meanwhile isn't lost.
    const { lines, notices } = reconcileLines(getState().cart.lines, items);
    if (JSON.stringify(lines) !== JSON.stringify(getState().cart.lines)) {
      dispatch(cartSlice.actions.replaceCart({ lines }));
    }
    return notices;
  },
);

const initialState: CartState = loadInitial();

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    addLine: (state, action: PayloadAction<Omit<CartLine, "line_id">>) => {
      const incoming = action.payload;
      const existing = state.lines.find(
        (l) => l.item_id === incoming.item_id && sameSelections(l.variant_selections, incoming.variant_selections),
      );
      if (existing) {
        existing.quantity = Math.min(99, existing.quantity + incoming.quantity);
      } else {
        state.lines.push({ ...incoming, line_id: uuid() });
      }
    },
    updateQuantity: (state, action: PayloadAction<{ line_id: string; quantity: number }>) => {
      const line = state.lines.find((l) => l.line_id === action.payload.line_id);
      if (!line) return;
      const q = Math.max(1, Math.min(99, Math.floor(action.payload.quantity)));
      line.quantity = q;
    },
    removeLine: (state, action: PayloadAction<string>) => {
      state.lines = state.lines.filter((l) => l.line_id !== action.payload);
    },
    clearCart: (state) => {
      state.lines = [];
    },
    /** The whole cart: another tab's saved copy, or a reconciled one. */
    replaceCart: (_state, action: PayloadAction<CartState>) => action.payload,
  },
});

export const { addLine, updateQuantity, removeLine, clearCart, replaceCart } = cartSlice.actions;

export default cartSlice.reducer;
